import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useBackTo } from "/imports/ui/components/discovery";
import api from "/imports/api/client";
import useFetch from "/imports/hooks/useFetch";
import usePackageOffers from "/imports/hooks/usePackageOffers";
import { offerRowKey } from "../PackagesView/packagesHelpers";
import { joinedWaitlistMessage } from "../ClassesView/classHelpers";

export default function useClassSession() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { slug, currency, addToCart } = useWidget();
  const { isAuthenticated, activeClientId } = useAuth();
  const goBack = useBackTo(`/${slug}/classes`);
  // A waitlist join/leave is patched into the loaded payload rather than
  // refetching the page (same as the timetable — see useClassBooking).
  const [waitlistPatch, setWaitlistPatch] = useState(null);
  // A second tap while a join is still in flight is ignored.
  const joiningWaitlist = useRef(false);

  // Keyed on auth so the roster (server-gated to logged-in viewers) appears
  // without a manual reload when the session state changes under this mount,
  // and on the acting client so eligibility follows the family switcher.
  const {
    data: fetched,
    loading,
    error
  } = useFetch(() => api.getClassDetail(slug, classId), [slug, classId, isAuthenticated, activeClientId], { skip: !slug || !classId });
  useEffect(() => setWaitlistPatch(null), [fetched]);
  const cls = useMemo(
    () => (fetched && waitlistPatch ? { ...fetched, waitlist: { ...(fetched.waitlist || {}), ...waitlistPatch } } : fetched),
    [fetched, waitlistPatch]
  );

  // Same cart hand-off as the timetable's join (useClassBooking.handleJoin) —
  // the detail payload carries the identical fields.
  const handleJoin = () => {
    if (!cls) return;
    addToCart("class", {
      type: "class",
      bookishId: cls._id,
      name: cls.name,
      date: cls.date,
      time: cls.time,
      duration: cls.duration,
      trainerId: cls.trainer?._id || null,
      trainerName: cls.trainer?.name || null,
      facilityId: cls.facility?._id || null,
      facilityName: cls.facility?.name || null,
      serviceId: cls.serviceId,
      pricingId: cls.pricingId,
      price: cls.price,
      currency,
      locationId: cls.location?._id || null,
      locationName: cls.location?.name || null
    });
    if (isAuthenticated) {
      navigate(`/${slug}/checkout`);
    } else {
      enqueueSnackbar("Please sign in to complete your booking", { variant: "warning" });
      navigate(`/${slug}/login?redirect=checkout`);
    }
  };

  const goLogin = () => navigate(`/${slug}/login?redirect=${encodeURIComponent(`classes/${classId}`)}`);

  // Class waitlist — same rules as the timetable's handlers (useClassBooking).
  const handleJoinWaitlist = async () => {
    if (joiningWaitlist.current) return;
    if (!isAuthenticated) {
      enqueueSnackbar("Please sign in to join the waitlist", { variant: "warning" });
      return goLogin();
    }
    joiningWaitlist.current = true;
    try {
      const res = await api.joinClassWaitlist(slug, classId);
      enqueueSnackbar(joinedWaitlistMessage(res), { variant: "success" });
      setWaitlistPatch({ isListed: true, position: res.position, count: res.count, entryId: res.entryId, isListFull: res.count >= res.maxSize });
    } catch (err) {
      enqueueSnackbar(err.message || "Could not join the waitlist", { variant: "error" });
    } finally {
      joiningWaitlist.current = false;
    }
  };

  const handleLeaveWaitlist = async () => {
    try {
      await api.leaveClassWaitlist(slug, classId);
      enqueueSnackbar("You've left the waitlist", { variant: "info" });
      const count = Math.max(0, (cls?.waitlist?.count || 1) - 1);
      setWaitlistPatch({ isListed: false, position: null, entryId: null, offer: null, count, isListFull: false });
    } catch (err) {
      enqueueSnackbar(err.message || "Could not leave the waitlist", { variant: "error" });
    }
  };

  // Same nudge the timetable carries, held back on the same terms: not on a
  // full or already-joined session, and not on one this client is barred from
  // — but only when we know who they are. Signed out, every level-gated class
  // reads "Assessment required", which says nothing about the visitor.
  const findPackageOffer = usePackageOffers(cls?.packageOffers);
  const packageOffer =
    cls && !cls.isPast && cls.spotsAvailable !== 0 && !cls.isJoined && !(isAuthenticated && cls.eligibility?.eligible === false)
      ? findPackageOffer(cls)
      : null;
  // joinAfter rides along so Plans can send the client back to this session
  // once the pack is paid for, instead of dropping them on the widget home.
  const handleViewPackage = () => {
    if (!packageOffer) return;
    const highlight = encodeURIComponent(offerRowKey(packageOffer));
    navigate(`/${slug}/plans?highlight=${highlight}&joinAfter=${encodeURIComponent(classId)}`);
  };

  return {
    slug,
    currency,
    cls,
    loading,
    error,
    goBack,
    handleJoin,
    handleJoinWaitlist,
    handleLeaveWaitlist,
    goLogin,
    navigate,
    packageOffer,
    handleViewPackage
  };
}
